import { useCallback, useState } from 'react'

import type { AdditionalCostType } from '@api'
import { CommandLoading } from 'cmdk'

import Check from '@/assets/icons/icon-check.svg'
import ChevronsUpDown from '@/assets/icons/icons-arrow-up-down.svg'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { CommandCreateItem } from '@/modules/accounting/ui/AdditionalCosts/CommandCreateItem.tsx'
import { classNames } from '@/utils/helpers'

import { useAccountingAdditionCostTypeMutations } from '../../hooks/useAccountingAdditionCostTypeMutations'
import { useAccountingAdditionalCostTypes } from '../../hooks/useAccountingAdditionalCostTypes'

interface Props {
  value: number | null
  onSelect: (typeId: number) => void
}

export const AdditionalCostTypeInput = ({ value, onSelect }: Props) => {
  const [open, setOpen] = useState(false)
  const { data, isLoading, refetch } = useAccountingAdditionalCostTypes()
  const { createCostType } = useAccountingAdditionCostTypeMutations()

  const handleSelect = (id: number) => {
    onSelect(id)
    setOpen(false)
  }

  const handleCreate = async (name: string) => {
    try {
      const result = await createCostType.mutateAsync({ name })

      if (result.id) {
        handleSelect(result.id)
        refetch()
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleFilter = useCallback((item: string, query: string) => {
    if (item === '+create+') {
      return 1
    }
    if (item.toLowerCase().includes(query.trim().toLowerCase())) {
      return 1
    }

    return 0
  }, [])

  const handleSelectItem = (item: AdditionalCostType) => () => {
    if (item.id) {
      onSelect(item.id)
      setOpen(false)
    }
  }

  return (
    <div className="relative flex items-center gap-1.5">
      <Popover
        open={open}
        onOpenChange={setOpen}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {value
              ? data?.find((option) => option.id === value)?.name
              : 'Select type'}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 fill-black opacity-50 dark:fill-white" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-100 w-[--radix-popover-trigger-width] p-0">
          <Command filter={handleFilter}>
            <CommandList>
              <CommandInput placeholder="Search type" />
              <CommandGroup>
                {isLoading && <CommandLoading>Loading...</CommandLoading>}
                {data?.map((option) => (
                  <CommandItem
                    key={option.id}
                    value={option.name}
                    onSelect={handleSelectItem(option)}
                  >
                    <Check
                      className={classNames(
                        'mr-2 h-4 w-4',
                        value === option.id ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {option.name}
                  </CommandItem>
                ))}
              </CommandGroup>

              <CommandCreateItem
                onSelect={handleCreate}
                loading={createCostType.isLoading}
              />
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
